import { useEffect, useState, useMemo } from 'react';
import { useReminderEngine, Reminder } from '@/hooks/useReminderEngine';
import { useLeads } from '@/hooks/useLeads';
import { useDeals } from '@/hooks/useDeals';
import { useCalendarEvents } from '@/hooks/useCalendarEvents';
import { useNotifications, useMarkNotificationRead, useDeleteNotification } from '@/hooks/useNotifications';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Bell,
  Clock,
  AlertTriangle,
  CheckCircle,
  X,
  Calendar,
  Loader2,
  Flame,
  DollarSign,
  ChevronRight,
} from 'lucide-react';
import { format, isToday, isTomorrow, isPast, addDays, differenceInHours } from 'date-fns';
import { cn } from '@/lib/utils';
import { Link, useNavigate } from 'react-router-dom';

interface Alert {
  id: string;
  type: 'hot' | 'overdue' | 'upcoming' | 'revenue';
  title: string;
  subtitle?: string;
  urgency: 'high' | 'medium' | 'low';
  href: string;
}

function AlertItem({ alert, onDismiss }: { alert: Alert; onDismiss: () => void }) {
  const navigate = useNavigate();
  
  const iconMap = {
    hot: Flame,
    overdue: AlertTriangle,
    upcoming: Calendar,
    revenue: DollarSign,
  };
  
  const Icon = iconMap[alert.type];
  
  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss();
  };
  
  return (
    <div 
      className={cn(
        "flex items-start gap-2 p-2 rounded-md border transition-all cursor-pointer group relative",
        "hover:border-primary/30 hover:bg-primary/5",
        alert.urgency === 'high' && "bg-destructive/5 border-destructive/20",
        alert.urgency === 'medium' && "bg-warning/5 border-warning/20",
        alert.urgency === 'low' && "bg-muted/50 border-border/50"
      )}
      onClick={() => {
        // Always navigate to Today for revenue-focused actions
        if (alert.urgency === 'high') {
          navigate('/app/today');
        } else {
          navigate(alert.href);
        }
      }}
    >
      <div className={cn(
        "w-6 h-6 rounded-md flex items-center justify-center shrink-0",
        alert.urgency === 'high' && "bg-destructive/10",
        alert.urgency === 'medium' && "bg-warning/10",
        alert.urgency === 'low' && "bg-muted"
      )}>
        <Icon className={cn(
          "w-3.5 h-3.5",
          alert.urgency === 'high' && "text-destructive",
          alert.urgency === 'medium' && "text-warning",
          alert.urgency === 'low' && "text-muted-foreground"
        )} />
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate leading-tight">{alert.title}</p>
        {alert.subtitle && (
          <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{alert.subtitle}</p>
        )}
      </div>
      
      <button
        onClick={handleDismiss}
        className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 p-0.5 hover:bg-destructive/10 rounded"
        aria-label="Dismiss notification"
      >
        <X className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
      </button>
      
      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-primary transition-colors shrink-0 mt-0.5" />
    </div>
  );
}

export function NotificationCenter() {
  const { data: leads } = useLeads();
  const { data: deals } = useDeals();
  const { data: calendarEvents } = useCalendarEvents(new Date(), 'week');
  const { data: persistentNotifications } = useNotifications({ isRead: false });
  const markAsRead = useMarkNotificationRead();
  const deleteNotification = useDeleteNotification();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  // Dev-only visibility check (helps diagnose RLS / missing inserts)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const now = new Date();
    const dueNow = (persistentNotifications || []).filter((n: any) => !n.is_read && (!n.scheduled_for || new Date(n.scheduled_for) <= now));
    const upcoming = (persistentNotifications || []).filter((n: any) => !n.is_read && n.scheduled_for && new Date(n.scheduled_for) > now);
    console.log('[NotificationCenter][dev] notifications fetched', {
      total: (persistentNotifications || []).length,
      dueNow: dueNow.length,
      upcoming: upcoming.length,
    });
  }, [persistentNotifications]);

  // Generate revenue-focused alerts (RARE AND SERIOUS ONLY)
  const alerts = useMemo(() => {
    const result: Alert[] = [];
    const now = new Date();
    const tomorrow = addDays(now, 1);
    
    // ELITE: ONLY show notifications if:
    // 1. Revenue at risk (€3K+ and overdue/never contacted)
    // 2. Decision blocked (€5K+ due today)
    // 3. Timing about to be lost (hot + overdue + €3K+)
    
    // High-value deals with revenue at risk (MUST ACT NOW) - ELITE: Only €3K+
    if (deals && leads) {
      deals.forEach(deal => {
        if (deal.value && deal.value >= 3000 && deal.stage !== 'won' && deal.stage !== 'lost') {
          const lead = leads.find(l => l.id === deal.lead_id);
          if (lead && lead.pipeline_stage !== 'won' && lead.pipeline_stage !== 'lost') {
            const followUpDate = (lead as any).next_follow_up_at 
              ? new Date((lead as any).next_follow_up_at) 
              : (lead.next_action_at ? new Date(lead.next_action_at) : null);
            
            const lastContact = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
            const neverContacted = !lastContact;
            const isOverdue = followUpDate && isPast(followUpDate);
            const isDueToday = followUpDate && isToday(followUpDate);
            
            // Revenue at risk: overdue or never contacted (ELITE: Only €3K+)
            if ((isOverdue || neverContacted) && deal.value >= 3000) {
              const valueK = `${(deal.value / 1000).toFixed(deal.value >= 10000 ? 0 : 1)}K`;
              result.push({
                id: `revenue-risk-${deal.id}`,
                type: 'revenue',
                title: `€${valueK} deal requires action today`,
                subtitle: neverContacted ? 'Never contacted' : 'Overdue follow-up',
                urgency: 'high',
                href: '/app/today',
              });
            }
            // Decision blocked: due today with high value (ELITE: Only €5K+)
            else if (isDueToday && deal.value >= 5000) {
              const valueK = `${(deal.value / 1000).toFixed(deal.value >= 10000 ? 0 : 1)}K`;
              result.push({
                id: `decision-blocked-${deal.id}`,
                type: 'hot',
                title: `€${valueK} deal requires action today`,
                subtitle: 'Timing about to be lost',
                urgency: 'high',
                href: '/app/today',
              });
            }
          }
        }
      });
    }
    
    // Hot leads with overdue follow-up (ELITE: Only if €3K+ deal)
    if (leads) {
      leads.forEach(lead => {
        if (lead.temperature === 'hot' && lead.pipeline_stage !== 'won' && lead.pipeline_stage !== 'lost') {
          const followUpDate = (lead as any).next_follow_up_at 
            ? new Date((lead as any).next_follow_up_at) 
            : (lead.next_action_at ? new Date(lead.next_action_at) : null);
          
          const lastContact = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
          const neverContacted = !lastContact;
          const isOverdue = followUpDate && isPast(followUpDate);
          
          // ELITE: Only if overdue or never contacted AND has €3K+ deal
          if ((isOverdue || neverContacted)) {
            const deal = deals?.find(d => d.lead_id === lead.id && d.stage !== 'won' && d.stage !== 'lost');
            if (deal && deal.value && deal.value >= 3000) {
              const valueK = `${(deal.value / 1000).toFixed(deal.value >= 10000 ? 0 : 1)}K`;
              result.push({
                id: `hot-risk-${lead.id}`,
                type: 'hot',
                title: `€${valueK} deal requires action today`,
                subtitle: neverContacted ? 'Never contacted' : 'Overdue follow-up',
                urgency: 'high',
                href: '/app/today',
              });
            }
          }
        }
      });
    }
    
    // Upcoming: ELITE - Only €5K+ events in next 4 hours
    if (calendarEvents) {
      calendarEvents.forEach(event => {
        const eventDate = new Date(event.start);
        const hoursUntil = differenceInHours(eventDate, now);
        
        // ELITE: Only show if €5K+ deal linked and within 4 hours
        if (hoursUntil >= 0 && hoursUntil <= 4 && event.leadId) {
          const deal = deals?.find(d => d.lead_id === event.leadId && d.stage !== 'won' && d.stage !== 'lost');
          if (deal && deal.value && deal.value >= 5000) {
            const valueK = `${(deal.value / 1000).toFixed(deal.value >= 10000 ? 0 : 1)}K`;
            result.push({
              id: `event-upcoming-${event.id}`,
              type: 'upcoming',
              title: `€${valueK} ${event.title}`,
              subtitle: format(eventDate, 'h:mm a'),
              urgency: hoursUntil <= 2 ? 'high' : 'medium',
              href: '/app/today',
            });
          }
        }
      });
    }
    
    // Add persistent notifications (deal close reminders) - include both due now and upcoming
    if (persistentNotifications) {
      persistentNotifications.forEach(notif => {
        const scheduledFor = notif.scheduled_for ? new Date(notif.scheduled_for) : null;
        const isDueNow = !scheduledFor || scheduledFor <= now;
        const isUpcoming = !!scheduledFor && scheduledFor > now;

        // Due now (unread & scheduled_for <= now OR null)
        if (isDueNow) {
          result.push({
            id: `persistent-${notif.id}`,
            type: notif.type === 'deal_close_today' ? 'revenue' : 'upcoming',
            title: notif.title,
            subtitle: notif.body || undefined,
            urgency: notif.type === 'deal_close_today' ? 'high' : 'medium',
            href: notif.deal_id ? `/app/pipeline` : notif.lead_id ? `/app/leads?id=${notif.lead_id}` : '/app/today',
          });
          return;
        }

        // Upcoming (scheduled_for > now) so reminders aren't invisible
        if (isUpcoming) {
          result.push({
            id: `persistent-${notif.id}`,
            type: 'upcoming',
            title: notif.title,
            subtitle: scheduledFor ? `Scheduled for ${format(scheduledFor, 'MMM d, h:mm a')}` : notif.body || undefined,
            urgency: 'low',
            href: notif.deal_id ? `/app/pipeline` : notif.lead_id ? `/app/leads?id=${notif.lead_id}` : '/app/today',
          });
        }
      });
    }
    
    // Filter dismissed and dedupe
    return result
      .filter(a => !dismissedIds.has(a.id))
      .slice(0, 10);
  }, [leads, deals, calendarEvents, dismissedIds, persistentNotifications]);

  const handleDismiss = (id: string) => {
    // If it's a persistent notification, mark as read or delete
    if (id.startsWith('persistent-')) {
      const notificationId = id.replace('persistent-', '');
      const notif = persistentNotifications?.find(n => n.id === notificationId);
      if (notif) {
        if (notif.type === 'deal_close_today') {
          // For today notifications, mark as read
          markAsRead.mutate(notificationId);
        } else {
          // For future reminders, delete (they'll be recreated if needed)
          deleteNotification.mutate(notificationId);
        }
      }
    } else {
      // In-memory alert, just dismiss
      setDismissedIds(prev => new Set([...prev, id]));
    }
  };

  const highPriorityCount = alerts.filter(a => a.urgency === 'high').length;
  const mediumPriorityCount = alerts.filter(a => a.urgency === 'medium').length;
  const hasAlerts = alerts.length > 0;
  const badgeColor = highPriorityCount > 0 ? 'bg-destructive' : mediumPriorityCount > 0 ? 'bg-warning' : '';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8">
          <Bell className="w-4 h-4" />
          {hasAlerts && (
            <span className={cn(
              "absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full flex items-center justify-center text-white",
              badgeColor
            )}>
              {alerts.length > 9 ? '9+' : alerts.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between p-3 border-b">
          <h4 className="font-semibold text-sm">Action Alerts</h4>
          {alerts.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 text-[10px] px-2" 
              onClick={() => setDismissedIds(new Set(alerts.map(a => a.id)))}
            >
              Clear all
            </Button>
          )}
        </div>

        <ScrollArea className="max-h-[60vh]">
          <div className="pr-4">
            {alerts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                <CheckCircle className="w-6 h-6 text-success/50 mb-2" />
                <p className="text-xs font-medium text-muted-foreground">No revenue at risk</p>
                <p className="text-[10px] text-muted-foreground/70 mt-1">You're on top of everything</p>
              </div>
            ) : (
              <div className="p-2 space-y-2">
              {/* Must Act Now */}
              {alerts.filter(a => a.urgency === 'high').length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between px-2">
                    <p className="text-[10px] font-bold text-destructive uppercase tracking-wide">
                      Must Act Now
                    </p>
                    <Link to="/app/today">
                      <Button variant="ghost" size="sm" className="h-5 text-[10px] px-2 gap-1">
                        Go to Today
                        <ChevronRight className="w-3 h-3" />
                      </Button>
                    </Link>
                  </div>
                  {alerts.filter(a => a.urgency === 'high').map(alert => (
                    <AlertItem 
                      key={alert.id} 
                      alert={alert} 
                      onDismiss={() => handleDismiss(alert.id)} 
                    />
                  ))}
                </div>
              )}
              
              {/* Upcoming */}
              {alerts.filter(a => a.urgency !== 'high').length > 0 && (
                <div className="space-y-1.5">
                  {alerts.filter(a => a.urgency === 'high').length > 0 && (
                    <div className="border-t border-border/50 pt-2" />
                  )}
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide px-2">
                    Upcoming
                  </p>
                  {alerts.filter(a => a.urgency !== 'high').map(alert => (
                    <AlertItem 
                      key={alert.id} 
                      alert={alert} 
                      onDismiss={() => handleDismiss(alert.id)} 
                    />
                  ))}
                </div>
              )}
              </div>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
